import { DatePicker, Flex, Image, Input, Select, TableProps } from "antd";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import BackIcon from "../../../../assets/icons/BackIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import placeholder from "../../../../assets/images/avatar.svg";
import StatusItem from "../../../../components/common/StatusItem";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import { dbDateFormat } from "../../../../constants/constants";
import {
  AllShiftParams,
  GetShiftDetailsMemberOverviewResponse,
} from "../../../../features/organization/contractual/ShiftManagement/shiftManagement.type";
import {
  useGetShiftDetailsMemberOverviewQuery,
  useGetShiftDetailsOverviewQuery,
} from "../../../../features/organization/contractual/ShiftManagement/shiftManagementApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../../utils/number";
import { valueOrNa } from "../../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../../utils/utils";
import CalanderIcon from "../../survey/templates/_icons/CalanderIcon";
import AbsentIcon from "./_icons/AbsentIcon";
import LateIcon from "./_icons/LateIcon";
import OnLeaveIcon from "./_icons/OnLeaveIcon";
import PresentTodayIcon from "./_icons/PresentTodayIcon";
import TotalEmployeeIcon from "./_icons/TotalEmployeeIcon";
const { RangePicker } = DatePicker;

export const formatTime = (timeString?: string) => {
  if (!timeString) return "-";
  return moment(timeString, "HH:mm:ss").format("hh:mm A");
};

export const STATUSES = [
  {
    label: "Absent",
    value: "absent",
    className: "status-danger rounded-lg",
  },
  {
    label: "Late",
    value: "late",
    className: "status-warning rounded-lg",
  },
  {
    label: "Leave",
    value: "leave",
    className: "status-warning rounded-lg",
  },
  {
    label: "Present",
    value: "present",
    className: "status-success rounded-lg",
  },
];

const ShiftEmployees = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useTable<AllShiftParams>();

  // --- Query ---
  const { data, isLoading, isFetching, isError, error } =
    useGetShiftDetailsOverviewQuery({
      id: id ? Number(id) : 0,
      args: {
        params: {},
      },
    });

  const {
    data: shiftDetailsMemberOverviewData,
    isFetching: shiftDetailsMemberOverviewIsFetching,
    isLoading: shiftDetailsMemberOverviewIsLoading,
    isError: shiftDetailsMemberOverviewIsError,
    error: shiftDetailsMemberOverviewError,
  } = useGetShiftDetailsMemberOverviewQuery({
    id: id ? Number(id) : 0,
    args: {
      params: {
        ...filter,
        search: keyword,
        limit,
      },
    },
  });

  // ---- Error Handling ----
  useError(isError, error, "Failed to fetch shift details overview");
  useError(
    shiftDetailsMemberOverviewIsError,
    shiftDetailsMemberOverviewError,
    "Failed to fetch shift details member overview"
  );

  const totalEmployees = data?.data?.number_of_employee_assigned
    ? data.data.number_of_employee_assigned
    : 0;
  const presentToday = data?.data?.attendance_summary?.present
    ? data.data.attendance_summary.present
    : 0;

  const lateToday = data?.data?.attendance_summary?.late
    ? data.data.attendance_summary.late
    : 0;
  const onLeaveToday = data?.data?.attendance_summary?.on_leave
    ? data.data.attendance_summary.on_leave
    : 0;
  const absentToday = data?.data?.attendance_summary?.absent
    ? data.data.attendance_summary.absent
    : 0;

  const STATES_CONFIG = [
    {
      icon: <TotalEmployeeIcon />,
      label: "Total Employees",
      value: totalEmployees,
    },
    {
      icon: <PresentTodayIcon />,
      label: "Present (Today)",
      value: presentToday,
    },
    {
      icon: <LateIcon />,
      label: "Late (Today)",
      value: lateToday,
    },
    {
      icon: <OnLeaveIcon />,
      label: "On Leave",
      value: onLeaveToday,
    },
    {
      icon: <AbsentIcon />,
      label: "Absent",
      value: absentToday,
    },
  ];

  const columns: TableProps<GetShiftDetailsMemberOverviewResponse>["columns"] =
    [
      {
        title: "#SL",
        align: "center",
        width: 60,
        render: (_, __, index) => countParPageSerialNumber(page, limit, index),
      },
      {
        title: "Bondhu Name",
        dataIndex: "name",
        render: (val) => (
          <Flex align="center" gap={6}>
            <Image
              width={38}
              height={38}
              src={placeholder}
              className="rounded-full"
            />
            <span>{val ? val : "n/a"}</span>
          </Flex>
        ),
      },
      {
        align: "center",
        title: "Bondhu ID",
        dataIndex: "employee_id",
        render: (val) => valueOrNa(val),
      },
      {
        title: "Job Title",
        dataIndex: "job_title",
        render: (val) => valueOrNa(val),
      },
      {
        align: "center",
        title: "Joining Date",
        dataIndex: "joining_date",
        render: (val) => formatDate(val),
      },
      {
        align: "center",
        title: "Attendance Date",
        dataIndex: "attendance_date",
        render: (val) => formatDate(val),
      },
      {
        align: "center",
        title: "Attendance",
        dataIndex: "present_count",
        render: (val) => `${addCommaInNumber(val || 0)} Day`,
      },
      {
        align: "center",
        title: "Status",
        dataIndex: "today_status",
        render: (val) => (
          <StatusItem
            value={`${val === "none" ? "n/a" : val}`}
            list={STATUSES}
          />
        ),
      },
    ];

  return (
    <SidebarLayout title="Shifts">
      {isLoading || isFetching ? (
        <Loader />
      ) : (
        <>
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate(-1)}>
              <BackIcon />
            </button>

            <div>
              <h4 className={`text-[#1C222B] text-[22px] font-medium`}>
                {data?.data?.shift?.shift_name}
              </h4>
              <p className="text-base font-normal text-body-text">
                {`${
                  data?.data?.shift?.from_date
                    ? formatDate(data?.data?.shift?.from_date, "DD MMM, YYYY")
                    : ""
                } – ${
                  data?.data?.shift?.to_date
                    ? formatDate(data?.data?.shift?.to_date)
                    : ""
                }`}{" "}
                • {formatTime(data?.data?.shift?.shift_start_time)} –{" "}
                {formatTime(data?.data?.shift?.shift_end_time)} •{" "}
                {data?.data?.shift?.number_of_seat
                  ? data?.data?.shift?.number_of_seat
                  : 0}{" "}
                Seats
              </p>
            </div>
          </div>

          <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 mb-6">
            {STATES_CONFIG.map((config) => (
              <div
                className="p-4 rounded-lg bg-white border border-extra-light"
                key={config.label}
              >
                <Flex align="center" gap={10}>
                  {config.icon}
                  <h6 className="text-base text-display-body font-medium">
                    {config.label}
                  </h6>
                </Flex>
                <h2 className="text-[28px] text-title font-normal">
                  {config.value}
                </h2>
              </div>
            ))}
          </div>

          <CustomTable
            loading={
              shiftDetailsMemberOverviewIsLoading ||
              shiftDetailsMemberOverviewIsFetching
            }
            limit={limit}
            page={page}
            columns={columns}
            total={shiftDetailsMemberOverviewData?.data?.total || 0}
            dataSource={addKeyInArray(
              shiftDetailsMemberOverviewData?.data?.members
            )}
            handleLimit={handleLimit}
            handlePagination={handlePagination}
            filterSection={
              <>
                <Input
                  size="middle"
                  suffix={<SearchIcon />}
                  placeholder="Search"
                  style={{ minWidth: 170, maxWidth: 180 }}
                  onChange={(val) => handleSearch(val)}
                  allowClear
                />

                <RangePicker
                  style={{ width: 245 }}
                  separator="-"
                  className="w-full input-bordered"
                  format={"DD/MM/YYYY"}
                  suffixIcon={<CalanderIcon />}
                  placeholder={["dd/mm/yyyy", "dd/mm/yyyy"]}
                  onChange={(dates: (Dayjs | null)[] | null) => {
                    const [start, end] = dates || [];

                    // Call filter only if both dates are selected
                    if (start && end) {
                      handleFilter(
                        "joining_date_from",
                        dayjs(start).format(dbDateFormat)
                      );
                      handleFilter(
                        "joining_date_to",
                        dayjs(end).format(dbDateFormat)
                      );
                    } else {
                      // Clear filters if user clears dates
                      handleFilter("joining_date_from", undefined);
                      handleFilter("joining_date_to", undefined);
                    }
                  }}
                />

                <Select
                  allowClear
                  showSearch
                  placeholder="Status"
                  className="min-w-[90px]"
                  rootClassName="icon-gray"
                  popupMatchSelectWidth={120}
                  suffixIcon={<SelectDownIcon />}
                  filterOption={selectSearchOption}
                  options={selectArrayOption(STATUSES, "label", "value")}
                  onChange={(value) => handleFilter("current_status", value)}
                />
              </>
            }
          />
        </>
      )}
    </SidebarLayout>
  );
};

export default ShiftEmployees;
